import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

function DatePicker({ 
  value, 
  onChange, 
  placeholder = "Pick a date",
  className,
  disabled = false
}) {
  const [open, setOpen] = React.useState(false)
  
  const date = value ? new Date(value) : undefined
  
  const handleSelect = (selectedDate) => {
    if (selectedDate) {
      const formattedDate = format(selectedDate, "yyyy-MM-dd")
      onChange(formattedDate)
    }
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          disabled={disabled}
          className={cn(
            "justify-start text-left font-normal",
            !date && "text-muted-foreground",
            className
          )}
          style={{ 
            backgroundColor: 'var(--input-bg)', 
            borderColor: 'var(--border-color)', 
            color: 'var(--text-primary)' 
          }}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, "dd MMM yyyy") : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}

function DatePickerInline({ 
  value, 
  onChange, 
  placeholder = "Select date",
  className,
  disabled = false,
  dateColor
}) {
  const [open, setOpen] = React.useState(false)
  
  const date = value ? new Date(value) : undefined
  const displayDate = date ? format(date, "dd MMM yy") : null;
  
  const handleSelect = (selectedDate) => {
    if (selectedDate) {
      const formattedDate = format(selectedDate, "yyyy-MM-dd")
      onChange(formattedDate)
    }
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          disabled={disabled}
          className={cn(
            "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-mono cursor-pointer transition-colors hover:opacity-80",
            className
          )}
          style={{ 
            color: dateColor || (displayDate ? 'var(--text-secondary)' : 'var(--text-disabled)'),
            background: 'transparent'
          }}
        >
          {displayDate || placeholder}
          <CalendarIcon className="w-3 h-3 opacity-40" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)', zIndex: 100 }}>
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}

function DatePickerCell({ 
  value, 
  onChange, 
  dateColor,
  disabled = false
}) {
  const [open, setOpen] = React.useState(false)
  
  const date = value ? new Date(value) : undefined
  const displayDate = date ? format(date, "dd MMM yy") : '—';
  
  const handleSelect = (selectedDate) => {
    if (selectedDate) {
      const formattedDate = format(selectedDate, "yyyy-MM-dd") + "T00:00:00.000Z"
      onChange(formattedDate)
    }
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span
          className="inline-flex items-center gap-0.5 font-mono cursor-pointer hover:underline"
          style={{ 
            color: dateColor || (date ? 'var(--text-secondary)' : 'var(--text-disabled)'),
            fontSize: 'inherit'
          }}
        >
          {displayDate}
          <CalendarIcon className="w-2.5 h-2.5 opacity-30" />
        </span>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" side="bottom" sideOffset={4} 
        style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)', zIndex: 100 }}>
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleSelect}
          disabled={disabled}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}

export { DatePicker, DatePickerInline, DatePickerCell }
